import { useState, useCallback, useRef, useEffect } from 'react';
import {
  BOARD_WIDTH,
  BOARD_HEIGHT,
  INITIAL_SPEED,
  LEVEL_SPEED_INCREASE,
  TETROMINOS,
  COLORS,
  PEPPER_SHAPES,
  PEPPER_COLORS,
  POWER_UPS,
  BASE_URL
} from './constants';

const createEmptyBoard = () => 
  Array.from({ length: BOARD_HEIGHT }, () => Array(BOARD_WIDTH).fill(null));

const createNewPepper = () => {
  const shapes = Object.keys(TETROMINOS);
  const randomShape = shapes[Math.floor(Math.random() * shapes.length)];
  return {
    shape: TETROMINOS[randomShape],
    color: COLORS[randomShape],
    x: Math.floor((BOARD_WIDTH - TETROMINOS[randomShape][0].length) / 2),
    y: 0,
    type: randomShape
  };
};

export const usePeprisGame = () => {
  // State
  const [gameState, setGameState] = useState(() => ({
    board: createEmptyBoard(),
    currentPepper: null,
    nextPepper: createNewPepper(),
    ghostPepper: null,
    clearedLines: [],
    score: 0,
    level: 1,
    gameOver: false,
    isPlaying: false,
    showInstructions: true
  }));

  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const [activePowerUp, setActivePowerUp] = useState(null);
  const [powerUpCooldown, setPowerUpCooldown] = useState(0);
  const [highScores, setHighScores] = useState([]);
  const [playerInfo, setPlayerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSoundOn, setIsSoundOn] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Refs voor audio
  const audioRefs = {
    background: useRef(null),
    row: useRef(null),
    buzz: useRef(null),
    clock: useRef(null),
    bonus: useRef(null),
  };

  // Core game functions
  const isCollision = useCallback((board, pepper) => {
    for (let y = 0; y < pepper.shape.length; y++) {
      for (let x = 0; x < pepper.shape[y].length; x++) {
        if (pepper.shape[y][x]) {
          const boardY = y + pepper.y;
          const boardX = x + pepper.x;
          if (
            boardY >= BOARD_HEIGHT ||
            boardX < 0 ||
            boardX >= BOARD_WIDTH ||
            (board[boardY] && board[boardY][boardX] !== null)
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }, []);

  const updateGhostPepper = useCallback((pepper, board) => {
    if (!pepper) return null;
    let ghostY = pepper.y;
    while (!isCollision(board, { ...pepper, y: ghostY + 1 })) {
      ghostY++;
    }
    return { ...pepper, y: ghostY };
  }, [isCollision]);

  const playSound = useCallback((sound) => {
    if (isSoundOn && audioRefs[sound]?.current) {
      audioRefs[sound].current.currentTime = 0;
      audioRefs[sound].current.play().catch(error => console.log('Audio play failed:', error));
    }
  }, [isSoundOn]);

  const checkPepperBonus = useCallback((board) => {
    for (const [pepperType, shape] of Object.entries(PEPPER_SHAPES)) {
      for (let y = 0; y <= BOARD_HEIGHT - shape.length; y++) {
        for (let x = 0; x <= BOARD_WIDTH - shape[0].length; x++) {
          const matchResult = isPepperComplete(board, x, y, shape, PEPPER_COLORS[pepperType]);
          if (matchResult.isComplete) {
            const baseBonus = 100 * shape.flat().filter(Boolean).length;
            const bonus = matchResult.isPerfect ? baseBonus * 2 : baseBonus;
            const newBoard = clearPepper(board, x, y, shape);
            playSound('bonus');
            return { newBoard, bonus };
          }
        }
      }
    }
    return { newBoard: board, bonus: 0 };
  }, [playSound]);

  const isPepperComplete = useCallback((board, startX, startY, shape, color) => {
    let isComplete = true;
    let isPerfect = true;
    for (let y = 0; y < shape.length; y++) {
      for (let x = 0; x < shape[y].length; x++) {
        if (shape[y][x]) {
          if (board[startY + y][startX + x] !== color) {
            isComplete = false;
            break;
          }
        } else {
          if (board[startY + y][startX + x] === color) {
            isPerfect = false;
          }
        }
      }
      if (!isComplete) break;
    }
    return { isComplete, isPerfect };
  }, []);

  const clearPepper = useCallback((board, startX, startY, shape) => {
    const newBoard = board.map(row => [...row]);
    for (let y = 0; y < shape.length; y++) {
      for (let x = 0; x < shape[y].length; x++) {
        if (shape[y][x]) {
          newBoard[startY + y][startX + x] = null;
        }
      }
    }
    return newBoard;
  }, []);

  const checkLines = useCallback((board) => {
    let linesToClear = [];
    board.forEach((row, index) => {
      if (row.every(cell => cell !== null)) {
        linesToClear.push(index);
      }
    });
    
    if (linesToClear.length > 0) {
      const newBoard = board.filter((_, index) => !linesToClear.includes(index));
      while (newBoard.length < BOARD_HEIGHT) {
        newBoard.unshift(Array(BOARD_WIDTH).fill(null));
      }
      playSound('row');
      
      // Reset clearedLines na korte vertraging
      setTimeout(() => {
        setGameState(prev => ({
          ...prev,
          clearedLines: []
        }));
      }, 50);
      
      return { newBoard, clearedLines: linesToClear };
    }
    return { newBoard: board, clearedLines: [] };
  }, [playSound]);

  const moveDown = useCallback(() => {
    setGameState(prevState => {
      if (!prevState.currentPepper || prevState.gameOver) return prevState;

      const newPepper = { ...prevState.currentPepper, y: prevState.currentPepper.y + 1 };
      
      if (isCollision(prevState.board, newPepper)) {
        let newBoard = prevState.board.map(row => [...row]);
        
        prevState.currentPepper.shape.forEach((row, y) => {
          row.forEach((value, x) => {
            if (value) {
              const boardY = y + prevState.currentPepper.y;
              const boardX = x + prevState.currentPepper.x;
              if (boardY >= 0 && boardY < BOARD_HEIGHT && boardX >= 0 && boardX < BOARD_WIDTH) {
                newBoard[boardY][boardX] = prevState.currentPepper.color;
              }
            }
          });
        });

        const { newBoard: bonusBoard, bonus } = checkPepperBonus(newBoard);
        const { newBoard: finalBoard, clearedLines } = checkLines(bonusBoard);

        const newScore = prevState.score + bonus + clearedLines.length * 100 * prevState.level;
        const newLevel = Math.floor(newScore / 1000) + 1;

        const nextPepper = prevState.nextPepper || createNewPepper();
        const newNextPepper = createNewPepper();

        if (isCollision(finalBoard, nextPepper)) {
          return { ...prevState, gameOver: true, board: finalBoard };
        }

        return {
          ...prevState,
          board: finalBoard,
          currentPepper: nextPepper,
          nextPepper: newNextPepper,
          ghostPepper: updateGhostPepper(nextPepper, finalBoard),
          clearedLines,
          score: newScore,
          level: newLevel
        };
      }

      return {
        ...prevState,
        currentPepper: newPepper,
        ghostPepper: updateGhostPepper(newPepper, prevState.board)
      };
    });
  }, [isCollision, checkPepperBonus, checkLines, updateGhostPepper]);

  const moveHorizontally = useCallback((direction) => {
    setGameState(prevState => {
      if (!prevState.currentPepper || prevState.gameOver) return prevState;

      const newPepper = {
        ...prevState.currentPepper,
        x: prevState.currentPepper.x + direction
      };
      
      if (!isCollision(prevState.board, newPepper)) {
        return {
          ...prevState,
          currentPepper: newPepper,
          ghostPepper: updateGhostPepper(newPepper, prevState.board)
        };
      }
      return prevState;
    });
  }, [isCollision, updateGhostPepper]);

  const rotatePepper = useCallback(() => {
    setGameState(prevState => {
      if (!prevState.currentPepper || prevState.gameOver) return prevState;

      const newShape = prevState.currentPepper.shape[0].map((_, index) =>
        prevState.currentPepper.shape.map(row => row[index]).reverse()
      );
      const newPepper = { ...prevState.currentPepper, shape: newShape };
      
      if (!isCollision(prevState.board, newPepper)) {
        return {
          ...prevState,
          currentPepper: newPepper,
          ghostPepper: updateGhostPepper(newPepper, prevState.board)
        };
      }
      return prevState;
    });
  }, [isCollision, updateGhostPepper]);

  const changeCurrentPepperColor = useCallback(() => {
    setGameState(prevState => {
      if (!prevState.currentPepper) return prevState;

      const colors = Object.values(COLORS);
      const currentIndex = colors.indexOf(prevState.currentPepper.color);
      const nextIndex = (currentIndex + 1) % colors.length;
      const newPepper = {
        ...prevState.currentPepper,
        color: colors[nextIndex]
      };
      
      return {
        ...prevState,
        currentPepper: newPepper,
        ghostPepper: updateGhostPepper(newPepper, prevState.board)
      };
    });
  }, [updateGhostPepper]);

  // Game control functions
  const startGame = useCallback(() => {
    setHasInteracted(true);
    if (!hasStartedPlaying && isSoundOn) {
      audioRefs.background.current?.play().catch(error => {
        console.log('Auto-play prevented:', error);
        // Als autoplay niet lukt, zetten we het geluid uit
        setIsSoundOn(false);
      });
      setHasStartedPlaying(true);
    }
    setGameState(prevState => ({
      ...prevState,
      board: createEmptyBoard(),
      currentPepper: createNewPepper(),
      nextPepper: createNewPepper(),
      ghostPepper: null,
      clearedLines: [],
      score: 0,
      level: 1,
      gameOver: false,
      isPlaying: true,
      showInstructions: false
    }));
  }, [hasStartedPlaying, isSoundOn]);

  const resetGame = useCallback(() => {
    startGame();
  }, [startGame]);

  const toggleSound = useCallback(() => {
    setIsSoundOn(prevState => {
      const newState = !prevState;
      if (newState && !hasStartedPlaying) {
        audioRefs.background.current?.play().catch(console.error);
        setHasStartedPlaying(true);
      } else if (newState && hasStartedPlaying) {
        audioRefs.background.current?.play();
      } else {
        audioRefs.background.current?.pause();
      }
      return newState;
    });
  }, [hasStartedPlaying]);

  // API functions
  const fetchHighScores = useCallback(() => {
    fetch('https://theuws.com/wp-json/game/v1/highscores?game=pepris', {
      method: 'GET',
      headers: {
        'Authorization': 'Bearer rVWU xf47 VvFt yl4T UmpQ MCAB'
      }
    })
      .then(response => response.ok ? response.json() : Promise.reject('Network response was not ok'))
      .then(setHighScores)
      .catch(error => console.error('Error fetching high scores:', error));
  }, []);

  const saveHighScore = useCallback(() => {
    fetch('https://theuws.com/wp-json/game/v1/highscores', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer rVWU xf47 VvFt yl4T UmpQ MCAB'
      },
      body: JSON.stringify({
        name: playerInfo.name,
        score: gameState.score,
        game: 'pepris',
        email: playerInfo.email,
        phone: playerInfo.phone
      })
    })
      .then(response => response.ok ? response.json() : Promise.reject('Network response was not ok'))
      .then(() => fetchHighScores())
      .catch(error => console.error('Error saving high score:', error));
  }, [gameState.score, playerInfo, fetchHighScores]);

  // Effects
  useEffect(() => {
    fetchHighScores();
  }, [fetchHighScores]);

  useEffect(() => {
    if (gameState.isPlaying && !gameState.gameOver) {
      const speed = Math.max(INITIAL_SPEED - (gameState.level - 1) * LEVEL_SPEED_INCREASE, 100);
      const intervalId = setInterval(moveDown, speed);
      return () => clearInterval(intervalId);
    }
  }, [gameState.isPlaying, gameState.gameOver, gameState.level, moveDown]);

  // Return game interface
  return {
    gameState,
    setGameState,
    playerInfo,
    setPlayerInfo,
    isSoundOn,
    setIsSoundOn,
    hasInteracted,
    setHasInteracted,
    audioRefs,
    moveDown,
    moveHorizontally,
    rotatePepper,
    changeCurrentPepperColor,
    startGame,
    resetGame,
    toggleSound,
    saveHighScore,
    fetchHighScores,
    highScores
  };
};